/**
 * Brands in the Audio Studio. Brand ids are Omni's (`useActiveBrand`); the sound service only stores them, so names and
 * logos come from here. `StudioBrandsProvider` (the page) reads the team's brands once and every row / card / dialog
 * looks them up through `useStudioBrands()` — outside the provider (other surfaces, unit tests) there are no brands and
 * every brand affordance hides itself.
 *
 *   - BrandChip: "For <logo> <Brand> ▾" on the dark banner; picking one sets the app-wide active brand.
 *   - BrandPills: "All brands" + one pill per brand (Your Collections filter).
 *   - BrandAvatar: the logo (or the initial on a soft purple square).
 *   - BrandPickerDialog: "Set brand…" / "Change brand…" — the brands plus "No brand".
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/components/ui/utils';
import { useActiveBrand } from '@/cowork/useActiveBrand';

export interface StudioBrand { brandId: string; brandName: string; brandLogoUrl?: string }

interface StudioBrands {
  brands: StudioBrand[];
  /** The app-wide active brand (undefined when the team has none). */
  activeBrandId: string | undefined;
  selectBrand: (id: string) => void;
}

const EMPTY: StudioBrands = { brands: [], activeBrandId: undefined, selectBrand: () => {} };
const BrandsContext = createContext<StudioBrands>(EMPTY);

export function StudioBrandsProvider({ children }: { children: ReactNode }) {
  const { brandId, brands, selectBrand } = useActiveBrand();
  const value = useMemo<StudioBrands>(() => ({
    brands: brands.map((b) => ({ brandId: b.brandId, brandName: b.brandName, brandLogoUrl: b.brandLogoUrl })),
    activeBrandId: brands.length ? brandId : undefined,
    selectBrand,
  }), [brands, brandId, selectBrand]);
  return <BrandsContext.Provider value={value}>{children}</BrandsContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- the hook belongs with its provider
export function useStudioBrands() {
  const ctx = useContext(BrandsContext);
  const byId = useMemo(() => new Map(ctx.brands.map((b) => [b.brandId, b])), [ctx.brands]);
  return {
    ...ctx,
    activeBrand: ctx.activeBrandId ? byId.get(ctx.activeBrandId) : undefined,
    /** The brand for an id, when it is one of the team's. */
    brandOf: (id: string | null | undefined) => (id ? byId.get(id) : undefined),
  };
}

export function BrandAvatar({ brand, size = 16, className }: { brand: StudioBrand; size?: number; className?: string }) {
  if (brand.brandLogoUrl) {
    return <img src={brand.brandLogoUrl} alt="" aria-hidden className={cn('shrink-0 rounded-[4px] bg-white object-contain', className)} style={{ width: size, height: size }} />;
  }
  return (
    <span
      aria-hidden
      className={cn('flex shrink-0 items-center justify-center rounded-[4px] bg-[#f4ebff] text-[#6941c6]', className)}
      style={{ width: size, height: size, fontSize: Math.max(8, size * 0.55), fontWeight: 700 }}
    >
      {brand.brandName.slice(0, 1).toUpperCase()}
    </span>
  );
}

/** The banner's brand switch. Hidden when the team has no brands. */
export function BrandChip() {
  const { brands, activeBrand, selectBrand } = useStudioBrands();
  if (!brands.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Brand: ${activeBrand?.brandName ?? 'none'}: change`}
          className="inline-flex h-7 max-w-[240px] items-center gap-1.5 rounded-full bg-white/15 pl-2 pr-2 text-[12.5px] leading-4 text-white outline-none transition-colors hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-[#a9f600]/60"
        >
          <span className="text-white/70">For</span>
          {activeBrand && <BrandAvatar brand={activeBrand} size={16} />}
          <span className="truncate" style={{ fontWeight: 600 }}>{activeBrand?.brandName ?? 'Pick a brand'}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-white/70" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[200px]">
        {brands.map((b) => (
          <DropdownMenuItem key={b.brandId} onSelect={() => selectBrand(b.brandId)}>
            <BrandAvatar brand={b} size={18} />
            <span className="min-w-0 flex-1 truncate">{b.brandName}</span>
            {b.brandId === activeBrand?.brandId && <Check className="h-4 w-4 text-[#6941c6]" aria-hidden />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** "All brands" + each brand; `value` null = all. Hidden when the team has no brands. */
export function BrandPills({ value, onChange, className }: { value: string | null; onChange: (id: string | null) => void; className?: string }) {
  const { brands } = useStudioBrands();
  if (!brands.length) return null;
  const pill = (on: boolean) => cn(
    'inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[13px] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#be43ff]/30',
    on ? 'border-[#2e024c] bg-[#2e024c] text-white' : 'border-[#eaecf0] bg-white text-[#344054] hover:bg-[#f9fafb]',
  );
  return (
    <div role="radiogroup" aria-label="Brand" className={cn('-mx-4 flex items-center gap-2 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden', className)}>
      <button type="button" role="radio" aria-checked={value === null} onClick={() => onChange(null)} className={pill(value === null)} style={{ fontWeight: 500 }}>All brands</button>
      {brands.map((b) => (
        <button key={b.brandId} type="button" role="radio" aria-checked={value === b.brandId} onClick={() => onChange(b.brandId)} className={pill(value === b.brandId)} style={{ fontWeight: 500 }}>
          <BrandAvatar brand={b} size={18} />
          {b.brandName}
        </button>
      ))}
    </div>
  );
}

/** Picks a brand (or "No brand") for a sound or a collection. `onPick("")` = no brand. */
export function BrandPickerDialog({ open, onOpenChange, title, subject, current, busy = false, onPick }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** What is being branded, e.g. “Coffee Jingle”. */
  subject: string;
  current: string | null | undefined;
  busy?: boolean;
  onPick: (brandId: string) => void;
}) {
  const { brands } = useStudioBrands();
  const row = (id: string, label: ReactNode, on: boolean) => (
    <li key={id || 'none'}>
      <button
        type="button"
        role="radio"
        aria-checked={on}
        disabled={busy}
        onClick={() => onPick(id)}
        className="flex h-10 w-full items-center gap-2.5 rounded-[8px] px-2 text-left text-[13.5px] text-[#101828] hover:bg-[#f9fafb] disabled:opacity-60"
      >
        {label}
        {on && <Check className="ml-auto h-4 w-4 shrink-0 text-[#6941c6]" aria-hidden />}
      </button>
    </li>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[16px]">{title} {busy && <Loader2 className="h-4 w-4 animate-spin text-[#98a2b3]" aria-hidden />}</DialogTitle>
          <DialogDescription className="truncate text-[12.5px]">{subject}</DialogDescription>
        </DialogHeader>
        <ul role="radiogroup" aria-label="Brands" className="-mx-1 max-h-[320px] space-y-0.5 overflow-y-auto">
          {brands.map((b) => row(b.brandId, <><BrandAvatar brand={b} size={20} /><span className="min-w-0 truncate" style={{ fontWeight: 500 }}>{b.brandName}</span></>, current === b.brandId))}
          {row('', <span className="text-[#667085]">No brand</span>, !current)}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
